import {
  AddTeamIcon,
  Alert02Icon,
  AlertCircleIcon,
  CheckmarkCircle01Icon,
  Delete02Icon,
  InformationCircleIcon,
  Loading01Icon,
  Mail01Icon,
  PencilEdit02Icon,
  ReloadIcon,
  SentIcon,
  SmartPhone01Icon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import { OverlayCard } from 'components/Cards/OverlayCards';
import { UserInfo } from 'components/Data/UserInfo';

import i18n from '../i18n';
import styles from './../components/HistoryRow/HistoryRow.module.scss';
import { CheckHistoryTypes } from './check-history-types';

/** `check_history.check_type` slugs for reference checks (matches HistoryRow routing). */
/**
 * Each action is defined as a function that takes a data object.
 * This data object contains all the values required to generate the icon and description.
 *
 */
const t = i18n.t.bind(i18n);

const icons = {
  customNote: PencilEdit02Icon,
  customInfo: InformationCircleIcon,
  customSuccess: CheckmarkCircle01Icon,
  customWarning: Alert02Icon,
  customError: AlertCircleIcon,
};

const customActionColors = {
  customNote: 'primary-4',
  customInfo: 'blue-2',
  customSuccess: 'green-2',
  customWarning: 'yellow-2',
  customError: 'red-2',
};

const getConsentTitle = consentKey => {
  return t(`users.consent.${consentKey}.title`);
};

/** Parses the `{field, from, to}` JSON a field-change history entry stores in `message`.
 * Returns null for older rows that predate this format, so callers can fall back gracefully. */
export const parseFieldChange = message => {
  if (!message) {
    return null;
  }
  try {
    const parsed = JSON.parse(message);
    return parsed && typeof parsed === 'object' && 'to' in parsed ? parsed : null;
  } catch (e) {
    return null;
  }
};

/** Generic JSON parse for the consolidated candidate-edit/replace history payloads. */
export const parseHistoryJSON = message => {
  if (!message) {
    return null;
  }
  try {
    const parsed = JSON.parse(message);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const createCustomAction =
  type =>
  ({ userName, entry, companyUsers, checkHuman, styles }) => ({
    icon: icons[type],
    color: customActionColors[type],
    description: (
      <p>
        {userName}
        {' • '}
        <span className={styles.wrap}>
          {entry?.message?.split(/(@\[.*?\]\(\d+\))/).map((part, index, array) => {
            const match = part.match(/@\[(.*?)\]\((\d+)\)/);
            if (match) {
              const [_, name, id] = match;
              const userInfo = companyUsers?.find(user => user.id === Number(id));
              return userInfo ? (
                <OverlayCard name={name} key={`${id}-${index}`} className={styles.userLink}>
                  <UserInfo
                    display={`${userInfo.first_name} ${userInfo.last_name}`}
                    email={userInfo.email}
                    firstName={userInfo.first_name}
                    lastName={userInfo.last_name}
                  />
                </OverlayCard>
              ) : (
                <span key={`${id}-${index}`}>{name}</span>
              );
            }
            return part;
          })}
        </span>
        {entry?.check_type && (
          <span>
            {/[.!?]\s*$/.test(entry?.message || '') ? ' ' : '. '}
            <b className={styles.userRef}>[{checkHuman}]</b>
          </span>
        )}
      </p>
    ),
  });

export const checkHistoryActions = {
  [CheckHistoryTypes.CompletedCheck]: ({ refCandidate, referenceLink }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        Your check is completed for {refCandidate}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedReferee]: ({ refReferee, country, referenceLink }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        Reference check completed by {refReferee} {country}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedRefereeIpError]: ({
    refReferee,
    country,
    refCandidate,
    referenceLink,
  }) => ({
    icon: Alert02Icon,
    color: 'yellow-2',
    description: (
      <p>
        Reference check completed by {refReferee} {country} using the same IP address as the
        candidate {refCandidate}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedCandidate]: ({
    refCandidate,
    candidateRefereeLinks,
    country,
    referees,
  }) =>
    referees && referees.length > 0
      ? {
          icon: CheckmarkCircle01Icon,
          color: 'green-2',
          description: (
            <p>
              {refCandidate} successfully provided referee details for{' '}
              <span>{candidateRefereeLinks}</span> {country}
            </p>
          ),
        }
      : {
          icon: CheckmarkCircle01Icon,
          color: 'green-2',
          description: (
            <p>
              {refCandidate} successfully provided referee details. {country}
            </p>
          ),
        },
  [CheckHistoryTypes.UpdatedEmail]: ({ userName, refCandidate, refReferee, referee, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: Mail01Icon,
      color: 'blue-2',
      description: (
        <p>
          {userName} updated {referee ? refReferee : refCandidate}'s email
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedContactNumber]: ({
    userName,
    refCandidate,
    refReferee,
    referee,
    entry,
  }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: SmartPhone01Icon,
      color: 'blue-2',
      description: (
        <p>
          {userName} updated {referee ? refReferee : refCandidate}'s phone number
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedRefereeDetails]: ({ userName, refReferee, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated {refReferee}'s {change?.field ? change.field.toLowerCase() : 'details'}
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.CandidateUpdatedRefereeDetails]: ({ userName, entry }) => {
    const data = parseHistoryJSON(entry?.message);
    const fieldNames = data?.changes?.map(change => change.field).join(', ');
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated referee <b className={styles.wrap}>{data?.referee}</b>'s{' '}
          {fieldNames || 'details'}
          {data?.reason && (
            <>
              {' '}
              with reason: "<span className={styles.wrap}>{data.reason}</span>"
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedCandidateDetails]: ({ userName, refCandidate, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated {refCandidate}'s{' '}
          {change?.field ? change.field.toLowerCase() : 'details'}
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.CandidateNotificationListToggled]: ({ userName, refCandidate, entry }) => ({
    icon: Mail01Icon,
    color: 'blue-2',
    description: (
      <p>
        {userName} {entry?.message === 'disabled' ? 'disabled' : 'enabled'} the notification list
        for {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedCandidateNotificationList]: ({ userName, refCandidate, entry }) => {
    const change = parseFieldChange(entry?.message);
    const added = change?.added?.length ?? 0;
    const removed = change?.removed?.length ?? 0;
    const parts = [];
    if (added) parts.push(`added ${added} email${added > 1 ? 's' : ''}`);
    if (removed) parts.push(`removed ${removed} email${removed > 1 ? 's' : ''}`);
    return {
      icon: Mail01Icon,
      color: 'blue-2',
      description: (
        <p>
          {userName} updated {refCandidate}'s notification list
          {parts.length ? ` — ${parts.join(', ')}` : ''}.
        </p>
      ),
    };
  },
  [CheckHistoryTypes.CandidateReplacedReferee]: ({ userName, entry }) => {
    const data = parseHistoryJSON(entry?.message);
    return {
      icon: AddTeamIcon,
      color: 'blue-2',
      description: (
        <p>
          {userName} replaced referee <b className={styles.wrap}>{data?.old_referee}</b> with{' '}
          <b className={styles.wrap}>{data?.new_referee}</b>
          {data?.reason && (
            <>
              {' '}
              with reason: "<span className={styles.wrap}>{data.reason}</span>"
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedDepartment]: ({ userName, refCandidate, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated {refCandidate}'s department information
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedJobRole]: ({ userName, refCandidate, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated {refCandidate}'s job title
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.UpdatedCandidateHiringStatus]: ({ userName, refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} marked {refCandidate} as hired.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestSentCandidate]: ({ userName, refCandidate }) => ({
    icon: Mail01Icon,
    color: 'orange-2',
    description: (
      <p>
        {userName} sent {refCandidate} a request email.
      </p>
    ),
  }),
  [CheckHistoryTypes.AdditionalChecksRequestSent]: ({ userName, refCandidate }) => ({
    icon: Mail01Icon,
    color: 'orange-2',
    description: (
      <p>
        {userName} sent {refCandidate} a request email.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestOverdueCandidate]: ({
    refCandidate,
    candidate,
    onCandidateRequest,
  }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        Check for {refCandidate} became overdue.{' '}
        {candidate.status === 'Candidate' && (
          <a onClick={() => onCandidateRequest()}>Resend request</a>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestSentReferee]: ({ userName, refReferee }) => ({
    icon: SentIcon,
    color: 'orange-2',
    description: (
      <p>
        {userName} sent referee {refReferee} a new request.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestOverdueReferee]: ({ refReferee, referee, onRefereeRequest }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        Reference request for referee {refReferee} overdue.{' '}
        {referee && !referee.completed_at && (
          <a onClick={() => onRefereeRequest(referee.id)}>Resend request</a>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateEmailFailed]: ({ refCandidate }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        Email couldn't be sent to {refCandidate}. You tried to send to a recipient that has been
        marked as inactive.
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeEmailFailed]: ({ refReferee }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        Email couldn't be sent to referee {refReferee}. You tried to send to a recipient that has
        been marked as inactive.
      </p>
    ),
  }),
  [CheckHistoryTypes.NewReferee]: ({ refCandidate, userName, refReferee, entry }) => ({
    icon: AddTeamIcon,
    color: 'primary-4',
    description: (
      <p>
        {entry.user_name ? (
          <>
            {userName} added new referee {refReferee}
          </>
        ) : (
          <>
            {refCandidate} nominated {refReferee}
          </>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.NewCheck]: ({ userName, checkHuman, refCandidate }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        {userName} created a new {checkHuman} for {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckSubmitted]: ({ refCandidate }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: <p>{refCandidate} has completed the background application.</p>,
  }),
  [CheckHistoryTypes.BackgroundCheckProcessing]: ({ refCandidate, checkHuman }) => ({
    icon: Loading01Icon,
    color: 'blue-2',
    description: (
      <p>
        {refCandidate}'s {checkHuman} is being processed.
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckCompleted]: ({ refCandidate, checkHuman }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        {refCandidate}'s {checkHuman} has been completed.
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckRejected]: ({ checkHuman, refCandidate }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {checkHuman} has been declined. New request sent to {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckUpdated]: ({ refCandidate, checkHuman }) => ({
    icon: ReloadIcon,
    color: 'blue-2',
    description: (
      <p>
        {refCandidate} updated {checkHuman} declined fields.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckCanceled]: ({ userName, refCandidate, checkHuman, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: entry?.message ? (
      <p>
        <span className={styles.wrap}>{entry.message}</span>
      </p>
    ) : (
      <p>
        {userName} canceled {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateSMSOpened]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refCandidate} has opened the SMS.</p>,
  }),
  [CheckHistoryTypes.RefereeSMSOpened]: ({ refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refReferee} has opened the SMS.</p>,
  }),
  [CheckHistoryTypes.CandidateSMSSent]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>SMS sent to {refCandidate}.</p>,
  }),
  [CheckHistoryTypes.RefereeSMSSent]: ({ refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>SMS sent to {refReferee}.</p>,
  }),
  [CheckHistoryTypes.CandidateEmailOpened]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refCandidate} has opened the email.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailOpened]: ({ refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refReferee} has opened the email.</p>,
  }),
  [CheckHistoryTypes.CandidateEmailClicked]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refCandidate} has clicked a link in the email.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailClicked]: ({ refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>{refReferee} has clicked a link in the email.</p>,
  }),
  [CheckHistoryTypes.CustomNote]: createCustomAction('customNote'),
  [CheckHistoryTypes.CustomInfo]: createCustomAction('customInfo'),
  [CheckHistoryTypes.CustomSuccess]: createCustomAction('customSuccess'),
  [CheckHistoryTypes.CustomWarning]: createCustomAction('customWarning'),
  [CheckHistoryTypes.CustomError]: createCustomAction('customError'),

  [CheckHistoryTypes.CheckDeclined]: ({ entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        Check declined with reason: "<span className={styles.wrap}>{entry.message}</span>"
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckAddNotes]: ({ refCandidate, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        Checkmate notes for {refCandidate}: "<span className={styles.wrap}>{entry.message}</span>"
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckCompleted]: ({ refCandidate, checkHuman }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        {refCandidate}'s {checkHuman} has a new partial result.
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckCanceled]: ({ userName, refCandidate, checkHuman, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: entry?.message ? (
      <p>
        <span className={styles.wrap}>{entry.message}</span>
      </p>
    ) : (
      <p>
        {userName} canceled {refCandidate}'s {checkHuman} sub check.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckUpdateStatus]: ({ userName, checkHuman, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName}{' '}
        <span className={styles.wrap}>
          marked the approval status of {checkHuman} as{' '}
          <span className="u-capitalize">"{entry.message?.replace('_', ' ')}"</span>
        </span>
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckUpdateStatus]: ({ userName, checkHuman, entry }) => {
    const [subCheckName, status] = entry.message?.split('||');
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description: (
        <p>
          {userName} marked the approval status of {subCheckName} from {checkHuman} as "
          {status?.replace('_', ' ')}"
        </p>
      ),
    };
  },
  [CheckHistoryTypes.RefereeDeleted]: ({ userName, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {userName} deleted referee{' '}
        <b className={cn(styles.userRef, 'u-padding-left--small')}>
          <span className={styles.wrap}>{entry.message}</span>
        </b>
        .
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeReplacement]: ({ userName, refCandidate, refReferee, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {userName} asked {refCandidate} to replace {refReferee}{' '}
        {entry.message && (
          <>
            with reason: "<span className={styles.wrap}>{entry.message}</span>"
          </>
        )}
        .
      </p>
    ),
  }),
  [CheckHistoryTypes.ReminderCandidate]: ({ userName, refCandidate, entry }) => {
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description:
        entry?.user_name && entry?.user_name?.trim().toLowerCase() !== 'user' ? (
          <p>
            Reminder sent to {refCandidate} by {userName}.
          </p>
        ) : (
          <p>Automated reminder sent to {refCandidate}.</p>
        ),
    };
  },
  [CheckHistoryTypes.ReminderReferee]: ({ userName, refReferee, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description:
      entry?.user_name && entry?.user_name?.trim().toLowerCase() !== 'user' ? (
        <p>
          Reminder sent to referee {refReferee} by&nbsp;{userName}.
        </p>
      ) : (
        <p>Automated reminder sent to referee {refReferee}.</p>
      ),
  }),
  [CheckHistoryTypes.UpdatedCandidateTeam]: ({ userName, refCandidate, entry }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: PencilEdit02Icon,
      color: 'primary-4',
      description: (
        <p>
          {userName} updated {refCandidate}'s team
          {change && (
            <>
              {' '}
              from <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
              <b className={styles.wrap}>{change.to}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.CheckResult]: ({ checkHuman, userName, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {entry.message ? (
          <>
            {entry.message} by <span style={{ paddingLeft: '3px' }}> {userName}</span>
          </>
        ) : (
          <>
            {checkHuman} report downloaded by{' '}
            <span style={{ paddingLeft: '3px' }}> {userName}</span>
          </>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.TransferUserAssignment]: ({ userName, entry, refCandidate }) => {
    const change = parseFieldChange(entry?.message);
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description: change ? (
        <p>
          {userName} reassigned {refCandidate} from{' '}
          <b className={styles.wrap}>{change.from || '—'}</b> to{' '}
          <b className={styles.wrap}>{change.to}</b>.
        </p>
      ) : (
        <p>
          {userName} assigned responsibility for {refCandidate} to {entry.message}.
        </p>
      ),
    };
  },
  [CheckHistoryTypes.NewUserAssignment]: ({ userName, entry, refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} assigned {refCandidate} to {entry.message}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckUndoCompletion]: ({ userName, refCandidate, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {userName} undid the completion of {refCandidate}'s {entry.check_type} check.
      </p>
    ),
  }),
  [CheckHistoryTypes.NewReferencePlusForm]: ({ entry }) => {
    const [index, refereeFormName, candidateFormName] = entry?.message?.split('::');
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description: entry?.message ? (
        <p>
          Referee #<b className={styles.userRef}>{index}&nbsp;</b> created for candidate with
          referee form{' '}
          <Link target="_blank" to={`/questionnaires/referees_reference`}>
            {refereeFormName}
          </Link>
          and candidate form{' '}
          <Link target="_blank" to={`/questionnaires/candidate_reference`}>
            {candidateFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference plus forms.</p>
      ),
    };
  },
  [CheckHistoryTypes.NewReferencePlusCommonForm]: ({ entry }) => {
    const [count, refereeFormName, candidateFormName] = entry?.message?.split('::');
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description: entry?.message ? (
        <p>
          <b className={styles.userRef}>
            {count} {Number(count) > 1 ? 'referees' : 'referee'}&nbsp;
          </b>{' '}
          created for candidate with referee form{' '}
          <Link target="_blank" to={`/questionnaires/referees_reference`}>
            {refereeFormName}
          </Link>
          and candidate form{' '}
          <Link target="_blank" to={`/questionnaires/candidate_reference`}>
            {candidateFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference plus forms.</p>
      ),
    };
  },
  [CheckHistoryTypes.NewReferenceForm]: ({ entry }) => {
    const [count, refereeFormName] = entry?.message?.split('::');
    return {
      icon: InformationCircleIcon,
      color: 'blue-2',
      description: entry?.message ? (
        <p>
          <b className={styles.userRef}>{count}&nbsp;</b> {count > 1 ? 'referees' : 'referee'}{' '}
          created for candidate with referee form{' '}
          <Link target="_blank" to={`/questionnaires/custom`}>
            {refereeFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference check form.</p>
      ),
    };
  },
  [CheckHistoryTypes.UserCompletedRefereeForm]: ({ userName, refCandidate, refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} answered the referee form on behalf of {refReferee}.
      </p>
    ),
  }),
  [CheckHistoryTypes.BundleCheckAdded]: ({ refCandidate, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        Checks were added using the bundle{' '}
        <span className="u-font-weight-600 primary-text">&nbsp;{entry?.message}&nbsp;</span> for{' '}
        {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedCandidateArchived]: ({ userName, refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {refCandidate} was archived by&nbsp;{userName}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeRequestSent]: ({ userName, refCandidate }) => ({
    icon: SentIcon,
    color: 'orange-2',
    description: (
      <p>
        {userName} asked {refCandidate} to nominate a new referee.
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateEmailDelivered]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>Email delivered to {refCandidate}.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailDelivered]: ({ refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>Email delivered to {refReferee}.</p>,
  }),
  [CheckHistoryTypes.CandidateRefereeBackupRequest]: ({ refCandidate, refReferee }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>A backup request was initiated for referee {refReferee}.</p>,
  }),
  [CheckHistoryTypes.ExternalBotMessage]: ({ entry, checkHuman, styles }) => ({
    icon: Loading01Icon,
    color: 'blue-2',
    description: (
      <p>
        {checkHuman && <span>{checkHuman} </span>}
        <span className={cn(styles.wrap)}>
          {entry?.message || 'Application with provider for processing.'}
        </span>
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateDocumentRequestSent]: ({ refCandidate, checkHuman, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>{entry?.message || `Document request sent to ${refCandidate} for ${checkHuman}`}</p>
    ),
  }),
  [CheckHistoryTypes.CandidateDocumentUploaded]: ({ refCandidate, checkHuman, entry }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: <p>{entry?.message || `${refCandidate} uploaded document for ${checkHuman}`}</p>,
  }),
  [CheckHistoryTypes.CheckBlocked]: ({ checkHuman, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        <b>{checkHuman}</b> blocked: {entry?.message || 'Candidate did not respond.'}
      </p>
    ),
  }),
  [CheckHistoryTypes.UserConsent]: ({ userName, entry }) => ({
    icon: InformationCircleIcon,
    description: (
      <p>
        {userName} accepted the consent for {getConsentTitle(entry.message)}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckAltIdPath]: ({ entry, styles }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        <span className={cn(styles.wrap)}>{entry?.message}</span>
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidatePortalInvitationSent]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: <p>A portal invitation was sent to {refCandidate}.</p>,
  }),
  [CheckHistoryTypes.CandidateCanceledCheck]: ({ entry, userName }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {entry?.message} was canceled by candidate {userName}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateCheckResultView]: ({ userName, checkHuman }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} viewed the result of {checkHuman} through the candidate portal.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckNotification]: ({ checkHuman, entry }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {checkHuman}: {entry?.message}
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateDataDeleted]: ({ refCandidate }) => ({
    icon: Delete02Icon,
    color: 'red-2',
    description: (
      <p>Personal data for {refCandidate} was automatically purged per the retention policy.</p>
    ),
  }),
  [CheckHistoryTypes.CheckResultFileDeleted]: ({ refCandidate, checkHuman }) => ({
    icon: Delete02Icon,
    color: 'red-2',
    description: (
      <p>
        Result file for {refCandidate}'s {checkHuman} was automatically purged per the retention
        policy.
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckResultFileDeleted]: ({ refCandidate, checkHuman }) => ({
    icon: Delete02Icon,
    color: 'red-2',
    description: (
      <p>
        Sub-check result file for {refCandidate}'s {checkHuman} was automatically purged per the
        retention policy.
      </p>
    ),
  }),

  [CheckHistoryTypes.ReConsentRequested]: ({ refCandidate, userName }) => ({
    icon: InformationCircleIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} sent a re-consent request to {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.ReConsentSubmitted]: ({ refCandidate }) => ({
    icon: InformationCircleIcon,
    color: 'green-2',
    description: <p>{refCandidate} reviewed and re-submitted their consent.</p>,
  }),

  [CheckHistoryTypes.RenewalConfigAssigned]: ({ userName, refCandidate, checkHuman, entry }) => {
    const data = parseHistoryJSON(entry?.message);
    const isAutomated = !entry?.user_name || entry?.user_name?.trim().toLowerCase() === 'user';
    return {
      icon: ReloadIcon,
      color: 'blue-2',
      description: (
        <p>
          {isAutomated ? (
            <>
              {refCandidate}'s {checkHuman} renewal was scheduled
            </>
          ) : (
            <>
              {userName} scheduled {refCandidate}'s {checkHuman} renewal
            </>
          )}
          {data?.renewal_date && (
            <>
              {' '}
              for <b className={styles.wrap}>{data.renewal_date}</b>
            </>
          )}
          .
        </p>
      ),
    };
  },
  [CheckHistoryTypes.RenewalConfigRemoved]: ({ userName, refCandidate, checkHuman, entry }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {userName} removed {refCandidate}'s {checkHuman} renewal configuration
        {entry?.message && (
          <>
            {' '}
            "<span className={styles.wrap}>{entry.message}</span>"
          </>
        )}
        .
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalInitiated]: ({ userName, refCandidate, checkHuman, entry }) => {
    const isAutomated = !entry?.user_name || entry?.user_name?.trim().toLowerCase() === 'user';
    return {
      icon: ReloadIcon,
      color: 'blue-2',
      description: isAutomated ? (
        <p>
          {refCandidate}'s {checkHuman} renewal was automatically initiated.
        </p>
      ) : (
        <p>
          {userName} initiated a renewal of {refCandidate}'s {checkHuman}.
        </p>
      ),
    };
  },
  [CheckHistoryTypes.RenewalApproved]: ({ userName, refCandidate, checkHuman }) => ({
    icon: CheckmarkCircle01Icon,
    color: 'green-2',
    description: (
      <p>
        {userName} approved the renewal for {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalStopped]: ({ userName, refCandidate, checkHuman }) => ({
    icon: AlertCircleIcon,
    color: 'red-2',
    description: (
      <p>
        {userName} stopped the renewal for {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalCanceled]: ({ userName, refCandidate, checkHuman }) => ({
    icon: Delete02Icon,
    color: 'red-2',
    description: (
      <p>
        {userName} canceled the renewal for {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalRestored]: ({ userName, refCandidate, checkHuman }) => ({
    icon: ReloadIcon,
    color: 'blue-2',
    description: (
      <p>
        {userName} restored the renewal for {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalConsentRequested]: ({ refCandidate, checkHuman }) => ({
    icon: Mail01Icon,
    color: 'orange-2',
    description: (
      <p>
        A renewal consent request was sent to {refCandidate} for {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RenewalConsentGiven]: ({ refCandidate, entry }) => {
    const data = parseHistoryJSON(entry?.message);
    const checkTypes = data?.check_types?.join(', ');
    return {
      icon: CheckmarkCircle01Icon,
      color: 'green-2',
      description: (
        <p>
          {refCandidate} gave consent for renewal{checkTypes ? ` of: ${checkTypes}` : ''}.
        </p>
      ),
    };
  },
  [CheckHistoryTypes.RenewalConsentDeclined]: ({ refCandidate, entry }) => {
    const data = parseHistoryJSON(entry?.message);
    const checkTypes = data?.check_types?.join(', ');
    return {
      icon: AlertCircleIcon,
      color: 'red-2',
      description: (
        <p>
          {refCandidate} declined consent for renewal{checkTypes ? ` of: ${checkTypes}` : ''}.
        </p>
      ),
    };
  },

  // Internal use only
  [CheckHistoryTypes.AdminGeneral]: createCustomAction('customNote'),
  [CheckHistoryTypes.AdminFollowUpProvider]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFollowUpCandidate]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFollowUpClient]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFeedback]: createCustomAction('customNote'),
  [CheckHistoryTypes.AdminAppointment]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminContact]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminAudit]: createCustomAction('customNote'),
};
